import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { FileDownloadResponse } from './file-response';
const c = initContract();
export const ProductTypeSchema = z.enum(['voice', 'data', 'sms-mms']);
export const AnalyticsChartItemSchema = z.object({
    /** "2024-01" (monthly) or "2024-01-15" (daily). */
    date: z.string(),
    /** Display label, e.g. "janv. 2024" or "lun. 15 janv.". */
    label: z.string(),
    /** Already converted: Go for data, minutes for voice, count for sms-mms. */
    value: z.number(),
    /** Number of calls (voice only). */
    quantity: z.number().optional(),
});
export const AnalyticsVoiceSummarySchema = z.object({
    totalCalls: z.number(),
    /** Formatted, e.g. "2h 34min". */
    totalDuration: z.string(),
    /** Formatted, e.g. "1min 23s". */
    avgCallDuration: z.string(),
});
export const AnalyticsDataSummarySchema = z.object({
    /** Formatted, e.g. "12.4 Go". */
    totalConsumption: z.string(),
    /** Formatted, e.g. "4.1 Go". */
    avgConsumption: z.string(),
    dataLimitMo: z.number().optional(),
});
export const AnalyticsSmsMmsSummarySchema = z.object({
    totalCount: z.number(),
});
export const AnalyticsUsageResponseSchema = z.object({
    items: z.array(AnalyticsChartItemSchema),
    granularity: z.enum(['daily', 'monthly']),
    voiceSummary: AnalyticsVoiceSummarySchema.optional(),
    dataSummary: AnalyticsDataSummarySchema.optional(),
    smsMmsSummary: AnalyticsSmsMmsSummarySchema.optional(),
});
export const AnalyticsLineSchema = z.object({
    id: z.string(),
    number: z.string(),
    status: z.string(),
    activationDate: z.string().nullable(),
    terminationDate: z.string().nullable(),
    identity: z
        .object({ firstName: z.string(), lastName: z.string() })
        .nullable(),
    phonePlan: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export const UnusedLineSchema = z.object({
    id: z.string(),
    number: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    phonePlanName: z.string().nullable(),
    status: z.string(),
});
/**
 * Express/qs delivers a repeated query param as `string[]`, but a single
 * occurrence as `string`. This helper normalizes both shapes to `string[]`.
 */
const coerceToStringArray = z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : [v]));
const optionalStringArray = coerceToStringArray
    .pipe(z.array(z.string()))
    .optional();
export const ListAnalyticsLinesQuerySchema = z.object({
    status: optionalStringArray,
    type: optionalStringArray,
    operator: optionalStringArray,
    identityId: z.string().optional(),
});
/** An omitted bound means "open on that side" (the backend picks defaults). */
const optionalWindowInt = z.preprocess((v) => (v === '' || v === null || v === undefined ? undefined : v), z.coerce.number().int().optional());
export const LineUsageQuerySchema = z.object({
    productType: ProductTypeSchema,
    startMonth: optionalWindowInt,
    startYear: optionalWindowInt,
    startDay: optionalWindowInt,
    endMonth: optionalWindowInt,
    endYear: optionalWindowInt,
    endDay: optionalWindowInt,
});
export const UnusedLinesQuerySchema = z.object({
    startMonth: z.coerce.number().int().min(1).max(12),
    startYear: z.coerce.number().int().min(2000),
    endMonth: z.coerce.number().int().min(1).max(12),
    endYear: z.coerce.number().int().min(2000),
});
export const ExportLineUsageBodySchema = z.object({
    startMonth: optionalWindowInt,
    startYear: optionalWindowInt,
    startDay: optionalWindowInt,
    endMonth: optionalWindowInt,
    endYear: optionalWindowInt,
    endDay: optionalWindowInt,
    productType: ProductTypeSchema,
    format: z.enum(['xlsx', 'csv']),
    delimiter: z.string().optional(),
});
/** The optional billing-centre scope travels in the body, not the path. */
export const ExportUnusedLinesBodySchema = z.object({
    siteId: z.string().optional(),
    startMonth: z.coerce.number().int().min(1).max(12),
    startYear: z.coerce.number().int().min(2000),
    endMonth: z.coerce.number().int().min(1).max(12),
    endYear: z.coerce.number().int().min(2000),
    format: z.enum(['xlsx', 'csv']),
    delimiter: z.string().optional(),
});
const orgParams = z.object({ organizationId: z.string().min(1) });
const siteParams = orgParams.extend({ siteId: z.string().min(1) });
/**
 * Analytics reads. Line usage is flat (the line UUID identifies the resource,
 * ACL enforced in the use case); listings are scoped org-wide or to a billing
 * centre via the path, like the lines listing. The streaming exports stay on
 * the legacy client (deferred register).
 */
export const analyticsContract = c.router({
    getLineUsage: {
        method: 'GET',
        path: '/lines/:lineId/usage',
        pathParams: z.object({ lineId: z.string().min(1) }),
        query: LineUsageQuerySchema,
        responses: { 200: AnalyticsUsageResponseSchema },
        summary: 'Chart-ready usage of a line (voice, data or sms-mms)',
    },
    listAnalyticsLines: {
        method: 'GET',
        path: '/organizations/:organizationId/analytics/lines',
        pathParams: orgParams,
        query: ListAnalyticsLinesQuerySchema,
        responses: { 200: z.array(AnalyticsLineSchema) },
        summary: 'Lightweight lines of an organization, for the analytics picker',
    },
    listSiteAnalyticsLines: {
        method: 'GET',
        path: '/organizations/:organizationId/sites/:siteId/analytics/lines',
        pathParams: siteParams,
        query: ListAnalyticsLinesQuerySchema,
        responses: { 200: z.array(AnalyticsLineSchema) },
        summary: 'Lightweight lines of a billing centre',
    },
    listUnusedLines: {
        method: 'GET',
        path: '/organizations/:organizationId/analytics/lines/unused',
        pathParams: orgParams,
        query: UnusedLinesQuerySchema,
        responses: { 200: z.array(UnusedLineSchema) },
        summary: 'Lines with no usage over the given window',
    },
    listSiteUnusedLines: {
        method: 'GET',
        path: '/organizations/:organizationId/sites/:siteId/analytics/lines/unused',
        pathParams: siteParams,
        query: UnusedLinesQuerySchema,
        responses: { 200: z.array(UnusedLineSchema) },
        summary: 'Lines of a billing centre with no usage over the given window',
    },
    // POST like the other exports: the file is a generated artifact, not a listing.
    exportLineUsage: {
        method: 'POST',
        path: '/lines/:lineId/usage/export',
        pathParams: z.object({ lineId: z.string().min(1) }),
        query: z.object({ organizationId: z.string().min(1) }),
        body: ExportLineUsageBodySchema,
        responses: { 200: FileDownloadResponse },
        summary: 'Export the usage of a line as xlsx or csv',
    },
    exportUnusedLines: {
        method: 'POST',
        path: '/organizations/:organizationId/analytics/lines/unused/export',
        pathParams: orgParams,
        body: ExportUnusedLinesBodySchema,
        responses: { 200: FileDownloadResponse },
        summary: 'Export the unused lines as xlsx or csv',
    },
});
